/**
 * Dashboard Grid Component
 *
 * Main grid layout for the dashboard with drag-and-drop support.
 * Uses CSS Grid for layout with @dnd-kit/sortable for reordering.
 *
 * @author BizClick WMS
 * @version 1.1.0
 */

import React, { useCallback, useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useDashboard } from '../DashboardContext';
import { WidgetInstance, DEFAULT_GRID_CONFIG } from '../types';
import DraggableWidget from './DraggableWidget';
import WidgetRenderer from './WidgetRenderer';

interface DashboardGridProps {
  className?: string;
}

const DashboardGrid: React.FC<DashboardGridProps> = ({ className = '' }) => {
  const { state, reorderWidgets } = useDashboard();
  const { currentLayout, isEditMode } = state;
  const [activeWidget, setActiveWidget] = React.useState<WidgetInstance | null>(null);

  // Configure drag sensors with better activation constraints
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8, // 8px movement before drag starts
    },
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 150,
      tolerance: 5,
    },
  });

  const sensors = useSensors(pointerSensor, mouseSensor, touchSensor);

  // Calculate grid dimensions
  const { gridColumns, gridRows } = currentLayout;
  const gap = DEFAULT_GRID_CONFIG.gap;

  // Get widget IDs for SortableContext
  const widgetIds = useMemo(
    () => currentLayout.widgets.map((w) => w.instanceId),
    [currentLayout.widgets]
  );

  // Handle drag start
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const widget = currentLayout.widgets.find(
        (w) => w.instanceId === event.active.id
      );
      setActiveWidget(widget || null);
    },
    [currentLayout.widgets]
  );

  // Handle drag end - reorder widgets
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveWidget(null);

      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const oldIndex = currentLayout.widgets.findIndex(
        (w) => w.instanceId === active.id
      );
      const newIndex = currentLayout.widgets.findIndex(
        (w) => w.instanceId === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(currentLayout.widgets, oldIndex, newIndex);
        reorderWidgets(newOrder);
      }
    },
    [currentLayout.widgets, reorderWidgets]
  );

  // Calculate cell style for each widget
  const getCellStyle = (widget: WidgetInstance) => {
    return {
      gridColumn: `span ${widget.size.width}`,
      gridRow: `span ${widget.size.height}`,
    };
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        className={`relative w-full h-full ${className}`}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gridAutoRows: `minmax(150px, 1fr)`,
          gap: `${gap}px`,
          padding: `${DEFAULT_GRID_CONFIG.padding}px`,
        }}
      >
        <SortableContext items={widgetIds} strategy={rectSortingStrategy}>
          {/* Widgets */}
          {currentLayout.widgets.map((widget) => (
            <div
              key={widget.instanceId}
              style={getCellStyle(widget)}
              className="min-h-0 min-w-0"
            >
              {isEditMode ? (
                <DraggableWidget widget={widget}>
                  <WidgetRenderer instance={widget} />
                </DraggableWidget>
              ) : (
                <WidgetRenderer instance={widget} />
              )}
            </div>
          ))}
        </SortableContext>

        {/* Empty state */}
        {currentLayout.widgets.length === 0 && (
          <div
            className="col-span-full row-span-full flex items-center justify-center"
            style={{
              gridColumn: `1 / -1`,
              gridRow: `1 / -1`,
              minHeight: '400px',
            }}
          >
            <div className="text-center text-gray-500">
              <p className="text-lg mb-2">Dashboard er tomt</p>
              <p className="text-sm">
                Klikk på "Rediger" og legg til widgets fra menyen
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Drag overlay - shows the widget being dragged */}
      <DragOverlay dropAnimation={null}>
        {activeWidget && (
          <div
            className="opacity-90 shadow-2xl rounded-lg overflow-hidden"
            style={{
              width: '300px',
              height: '200px',
            }}
          >
            <WidgetRenderer instance={activeWidget} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default DashboardGrid;
